import { memo } from 'react';
import DOMPurify from 'dompurify';
import { Alert, Divider } from 'antd';
import { SimpleModal } from '@/components/modals';
import { IoAlertCircleOutline } from 'react-icons/io5';
import { useModal, useDevice } from '@/hooks';
import { Flex, LoadingSpin, TryAgain } from '@/components';
import {
  Swiper,
  SwiperSlide,
  SwiperOutNav,
  Navigation,
  getDefaultNavigation
} from '@/components/common/Swiper';
import { useTranslation } from 'react-i18next';
import { useAnnouncementStore } from '@/store';

export const AnnouncementsList = memo(() => {
  const modal = useModal();
  const { isDesktop } = useDevice();
  const [t] = useTranslation(['common']);
  const [announcements, fetchAnnouncements, error, loading] = useAnnouncementStore(s => [s.announcements, s.fetchAnnouncements, s.error, s.loading]);

  if (loading) return <LoadingSpin text={t('common:labels.loading-data')} />;
  if (error) return <TryAgain text={error} onRetry={fetchAnnouncements} />;
  return (
    <>
      <SimpleModal
        hide={modal.hide}
        open={modal.open}
        title={modal.data?.title}
        content={DOMPurify.sanitize(modal?.data?.mainText, { ALLOWED_TAGS: ['b', 'a', 'i'] })}
      />
      <div className="flex-center flex-col py-3 mt-auto">
        <span className="text-base font-bold w-full text-start" style={{ paddingRight: '2rem' }}>
          {t('common:labels.announcements')}
        </span>
        <Divider style={{ marginBlock: '0.5rem', borderWidth: '2px', borderColor: '#979797' }} />
        <div className="flex justify-between w-full relative min-h-[70px] overflow-hidden">
          {announcements.length === 0 ?
            (
              <Flex className="flex flex-col w-full">
                <Alert className="flex w-full m-auto" type="warning" message={t('common:messages.no-announcement')} />
              </Flex>
            ) :
            (<>
              <SwiperOutNav id="swiper-announcement-nav" />
              <Swiper
                spaceBetween={0}
                modules={[Navigation]}
                className="!h-[135px] w-full"
                slidesPerView={isDesktop() ? 3 : 2}
                navigation={getDefaultNavigation('swiper-announcement-nav')}
              >
                {announcements.map((message, key) => (
                  <SwiperSlide key={key} className="!items-start hover:bg-app-gray-extra-light transition rounded-md">
                    <div
                      onClick={() => modal.show(message)}
                      className="w-90 h-full flex cursor-pointer !text-justify">
                      <IoAlertCircleOutline
                        size="2rem"
                        color={'#43bf58'}
                        style={{ minWidth: '2rem' }}
                      />
                      <div className="w-full relative px-2 flex flex-col">
                        <span className="inline-flex text-base py-1 text-app-gray-darker font-bold">{message.title}</span>
                        <span
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message?.mainText, { ALLOWED_TAGS: [] }) }}
                          className="flex-1 text-sm text-app-gray-darker mb-0 line-clamp-3"
                          style={{ wordBreak: 'break-word' }}
                        />
                        <span className="text-[#2F93F0] font-bold flex justify-end">
                          {t('common:labels.more')}
                        </span>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </>)
          }
        </div>
      </div>
    </>
  );
});

export default AnnouncementsList;
